import { ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';
import { mergeQueryString, QueryCombiner, SerializedData, updateSearch } from 'bernie-core';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { ILocalization, LocalizedComponentProps, useLocalization } from '@shared-ui/localization-context';
import { PropertyActivitiesCrossSellContextProvider } from '@shared-ui/retail-lodging-property-activities-cross-sell';
import { PropertyOffers } from '@shared-ui/lodging-property-offers';
import { RefTarget } from '@shared-ui/ref-context';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { MultiItemStore } from 'stores/multi-item-store';
import { rewriteHistory } from 'utils/route-store-utils';
import { SearchCriteriaModel, singleUseQueryParams } from 'stores/models';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { SearchResultsStore } from 'stores/search-results-store';

import { Level, liveAnnounce } from 'uitk-react-utils';
import { UitkSpacing } from 'uitk-react-spacing';

import { multiItemCompleteSearchCriteria } from 'components/multi-item/utils';
import { readFromCachedFragment } from 'utils/read-from-cached-fragment';

import { FocusTarget } from 'components/common/focus/focus-target';
import { gql, useApolloClient } from '@apollo/client';
import { History, Location } from 'history';
import { PropertyActivitiesCrossSell } from 'components/infosite/template-components/property-activities-cross-sell-wrapper';
import { SearchWizardComposer } from './search-wizard-composer';
import { SharedUiBookByPhoneWrapper } from 'components/infosite/shared-ui/details/book-by-phone-wrapper';
import { SharedUiLodgingOneKeyBurnSwitchWrapper } from '../shared-ui/offers/lodging-one-key-burn-switch-wrapper';
import { SharedUiLoyaltyDiscountToggleWrapper } from '../shared-ui/offers/loyalty-discount-toggle-wrapper';
import { SharedUiOffersWrapper } from 'components/infosite/shared-ui/offers/offers-wrapper';
import { SharedUiTravelersLinkWrapper } from 'components/infosite/shared-ui/form/field-wrappers';
import { SimpleLazyLoadWrapper } from 'components/common/lazy/simple-lazy-load-wrapper';
import { StickyBookBar } from 'components/infosite/template-components/sticky-book-bar';

export const CACHED_PROPERTY_OFFERS_A11Y_LABEL_FRAGMENT = gql`
  fragment LoadingA11yLabelFragment on OfferDetails {
    loading {
      accessibilityLabel
    }
  }
`;

export interface RoomsAndRatesLocalProps extends RouteComponentProps {
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  multiItem?: MultiItemStore;
  searchResults?: SearchResultsStore;
  experiment?: ExperimentStore;
}

type LocalizedRoomsAndRatesLocalProps = LocalizedComponentProps & RoomsAndRatesLocalProps;

export const RoomsAndRates: React.FC<LocalizedRoomsAndRatesLocalProps> = inject(
  'currentHotel',
  'context',
  'multiItem',
  'searchResults',
  'experiment'
)(
  observer(({ currentHotel, context, multiItem, searchResults }) => {
    const l10n = useLocalization();
    const history = useHistory();
    const location = useLocation();
    const client = useApolloClient();
    const propertyOffers = readFromCachedFragment(
      client,
      `OfferDetails:${currentHotel?.hotelId}`,
      CACHED_PROPERTY_OFFERS_A11Y_LABEL_FRAGMENT
    );
    const accessibilityLabel = propertyOffers?.loading?.accessibilityLabel;

    const onUpdateAvailabilityTravelers = (fields: { [name: string]: string }) => {
      currentHotel?.setOfferData(fields);
      onUpdateResultsAvailabilityWizard(undefined, currentHotel?.offerSearchData, true);
    };

    const onUpdateResultsAvailabilityWizard = (event?: Event, offerSearchData?: SerializedData, goBack = true) => {
      handleUpdateResults({
        accessibilityLabel,
        context,
        searchResults,
        l10n,
        history,
        location,
        event,
        criteria: {
          ...currentHotel?.searchCriteria.toJSON(multiItemCompleteSearchCriteria),
          ...SearchQueryAdapter.parseIn(offerSearchData),
        },
        goBack,
      });
    };

    const onUpdateResults = (event: Event, searchCriteria: SearchCriteriaModel, goBack = true) => {
      handleUpdateResults({
        accessibilityLabel,
        context,
        searchResults,
        l10n,
        history,
        location,
        event,
        criteria: searchCriteria.toJSON(multiItemCompleteSearchCriteria),
        goBack,
        queryCombiner: (prevQuery, newQuery) => newQuery,
      });
    };

    React.useEffect(() => {
      const { startDate, endDate, adults, children } = SearchQueryAdapter.parseOut(currentHotel?.searchCriteria);

      // 0 - children from search is undefined
      const childrenFormatted = typeof children === 'undefined' ? '' : children;

      currentHotel?.setOfferData({ startDate, endDate, adults, children: childrenFormatted });
    }, [currentHotel]);

    const isMultiItem = multiItem && multiItem.isPackageShoppingPath();

    const searchWizardComposerProps = {
      currentHotel,
      isMultiItem,
      onUpdateResults,
      onUpdateResultsAvailabilityWizard,
    };

    const roomsAndRatesComponent = (
      <>
        <UitkSpacing padding={{ blockend: 'three' }}>
          <SharedUiBookByPhoneWrapper />
        </UitkSpacing>
        <PropertyActivitiesCrossSellContextProvider>
          <SharedUiOffersWrapper>
            <PropertyOffers.HeaderRight>
              {!isMultiItem && (
                <Viewport>
                  <ViewSmall>
                    <SharedUiTravelersLinkWrapper handleChange={onUpdateAvailabilityTravelers} />
                  </ViewSmall>
                  <ViewLarge />
                </Viewport>
              )}
            </PropertyOffers.HeaderRight>
            <PropertyOffers.Form>
              <div>
                <SearchWizardComposer {...searchWizardComposerProps} />
                <SharedUiLoyaltyDiscountToggleWrapper />
                <SharedUiLodgingOneKeyBurnSwitchWrapper showBorder={false} />
              </div>
            </PropertyOffers.Form>
          </SharedUiOffersWrapper>
          <PropertyActivitiesCrossSell />
        </PropertyActivitiesCrossSellContextProvider>
        <Viewport>
          <ViewSmall>
            <div data-stid="sticky-book-bar">
              <StickyBookBar />
            </div>
          </ViewSmall>
          <ViewLarge>
            <React.Fragment />
          </ViewLarge>
        </Viewport>
      </>
    );

    return (
      <ErrorBoundary label="hotel.information.rooms.roomsAndRates">
        <FocusTarget targetKey="searchForm">
          <UitkSpacing margin={{ blockstart: 'six' }}>
            <div data-stid="rooms-rates">
              <RefTarget name="Offers" tabIndex={-1}>
                <Experiment name="Dated_PDP_Lazy_Loading_Rooms_Module">
                  <ExperimentControl>{roomsAndRatesComponent}</ExperimentControl>
                  <ExperimentVariant bucket={1}>
                    <SimpleLazyLoadWrapper>{roomsAndRatesComponent}</SimpleLazyLoadWrapper>
                  </ExperimentVariant>
                </Experiment>
              </RefTarget>
            </div>
          </UitkSpacing>
        </FocusTarget>
      </ErrorBoundary>
    );
  })
);

interface HandleUpdateResultsInput {
  accessibilityLabel: string;
  context?: ContextStore;
  searchResults?: SearchResultsStore;
  l10n: ILocalization;
  history: History;
  location: Location;
  event?: Event;
  criteria: SerializedData;
  goBack: boolean;
  queryCombiner?: QueryCombiner;
}

export const handleUpdateResults = ({
  accessibilityLabel,
  context,
  searchResults,
  l10n,
  history,
  location,
  event,
  criteria,
  goBack = true,
  queryCombiner,
}: HandleUpdateResultsInput) => {
  if (event) {
    event.preventDefault();
  }

  const keysToKeepFromOriginalQuery = (key: string) =>
    !key.startsWith('pwaDialog') && !key.startsWith('rm') && !singleUseQueryParams.includes(key);

  const params = { ...criteria, localDateFormat: context?.localDateFormat };

  const queryString = mergeQueryString(location.search, InfositeQueryAdapter.parseOut(params, l10n), keysToKeepFromOriginalQuery);

  searchResults?.clearDefaultRequestFields();

  if (goBack) {
    rewriteHistory(history, { ...location, search: queryString });
  } else {
    liveAnnounce(accessibilityLabel, Level.POLITE);
    updateSearch({ history, location, newParams: queryString, replace: true, queryCombiner });
  }
};
